import {
  useEvent,
  useEventsOnViewLoad
} from '@emerald-works/react-event-bus-client'
import {
  Button,
  Paper,
  Grid,
  Divider,
  Typography,
  CircularProgress,
  Box
} from '@mui/material'
import { useDispatch } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { ArrowBack } from '@mui/icons-material'
import { UserListSlice, MSATenantConfigSlice } from '../../../../reducers'
import { parseISO } from 'date-fns'
import ToggleUser from '../toggle-user'
import { useStyles } from './style'
import RoleSelector from '../user-roles'
import PasswordReset from './password-reset'
import { ADMIN, ALL_GROUPS } from '../../../../util/userGroups'
import { ActionButton } from '@emerald-works-nova/components'
import { useForm } from 'react-hook-form'
import { Input } from '../../../consumer-edit/components'
import { BannerStatic } from '../../../../components'
import { useExternalApp } from '../../../../hooks'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useArkMode } from '../../../../hooks/useArkMode'
import UserFeatures from './user-features'
import { buildInitialFeaturesState, buildFeaturesPayload } from './user-features/utils'

const COMPONENT_MODE = {
  DETAIL: 'DETAIL',
  EDIT: 'EDIT'
}

const userFormSchema = yup.object().shape({
  givenName: yup.string().required('First name is required'),
  familyName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required')
})

const UserDetail = ({ user, userPoolId, onBackToListClick, tenantId }) => {
  const dispatch = useDispatch()
  const [enabled, setEnabled] = useState(user.enabled)
  const [userDetail, setUserDetail] = useState()
  const [userFeatures, setUserFeatures] = useState()
  const [mode, setMode] = useState(COMPONENT_MODE.DETAIL)
  const { isAdminMode } = useArkMode()
  const [role, setRole] = useState()
  const [roles, setRoles] = useState()
  const form = useForm({
    resolver: yupResolver(userFormSchema)
  })

  const { baseToolkitUrl } = useExternalApp({ excludeProtocol: true })

  const isEdit = mode === COMPONENT_MODE.EDIT
  const filterRoles = rolesList =>
    rolesList
      .filter(x => ALL_GROUPS.includes(x.GroupName))
      .filter(x => x.GroupName !== ADMIN)
      .map(x => x.GroupName)

  const [adminUpdateUser, updateUserFeatures] = useEvent([{
    eventName: 'adminUpdateUser',
    onSuccess: (payload) => {
      const { attributes: { email, given_name: givenName, family_name: familyName }, username, errors } = payload
      const label = errors.length > 0 ? `User updated with errors: ${errors.toString()}` : 'User updated successfully'
      BannerStatic.show({ label })
      setMode(COMPONENT_MODE.DETAIL)
      setUserDetail(detail => {
        const updatedUser = { ...detail, email, givenName, familyName }
        return updatedUser
      })
      dispatch(UserListSlice.actions.updateUserDetails({ email, givenName, familyName, username }))
    },
    onError: e => {
      console.error("Error while attempting to update the user's details", e)
      BannerStatic.show({
        label: "Error while attempting to update the user's details",
        isError: true
      })
    }
  },
  {
    eventName: 'adminUpdateUserFeatures',
    onSuccess: (payload) => {
      BannerStatic.show({
        label: 'User permissions updated successfully'
      })
      dispatch(MSATenantConfigSlice.actions.updateUserOnList(payload))
      dispatch(UserListSlice.actions.updateUserPermissionsInList({ username: user.username, updatedFeatures: payload.updatedFeatures }))
      setFeatureIsDirty(false)
    },
    onError: (e) => {
      console.error('adminUpdateUserFeatures ', e)
      BannerStatic.show({
        label: 'Unable to update user permissions',
        isError: true
      })
    }
  }])

  const [getUserDetails, getUserRoles, getUserFeatures] = useEvent([
    {
      eventName: 'adminGetUserCognito',
      onSuccess: payload => {
        setUserDetail(payload)
        setUserFeatures(payload.features)
        form.setValue('givenName', payload.givenName)
        form.setValue('familyName', payload.familyName)
        form.setValue('email', payload.email)
      }
    },
    {
      eventName: 'adminGetUserRoles',
      onSuccess: payload => {
        setRole(payload.role[0])
        setRoles(filterRoles(payload.roles.Groups))
      }
    },
    {
      eventName: 'adminGetUserCognito',
      onSuccess: payload => {
        setUserFeatures(payload.features)
      }
    }
  ])

  const isLoading = React.useMemo(() =>
    getUserDetails.isWorking ||
    getUserRoles.isWorking, [getUserDetails.isWorking, getUserRoles.isWorking]
  )

  const isSSOUser =
    userDetail?.identities &&
    JSON.parse(userDetail.identities).find(
      x => x.providerType === 'SAML' && x.providerName !== 'internal'
    )

  const userHasWeeklySubscription =
    user?.profile?.emailContactId && (
      !user.profile.newsletter || user?.profile?.newsletter?.emailWeeklySubscription
    )

  const classes = useStyles({
    enabled,
    isLoading: isLoading,
    suscribed: userHasWeeklySubscription || false,
    rows: userDetail?.custom?.length
  })

  useEventsOnViewLoad(() => {
    dispatch(UserListSlice.actions.resetUserDetails())
    getUserDetails.trigger({ userPoolId, username: user.username, extraAttributes: ['roles'] })
    getUserRoles.trigger({ userPoolId, username: user.username })
  }, [getUserDetails, getUserRoles, dispatch])

  const [featureIsDirty, setFeatureIsDirty] = useState(false)
  const [features, setFeatures] = useState([])

  useEffect(() => {
    if (role) {
      setFeatures(buildInitialFeaturesState(role))
    }
  }, [role])

  const onRoleChanged = (role) => {
    const hasFeatures = buildInitialFeaturesState(role).length

    if (hasFeatures) {
      getUserFeatures.trigger({ userPoolId, username: user.username, extraAttributes: ['roles'] })
    }
  }

  const renderCustom = () =>
    userDetail.custom.map((att, index, arr) => (
      <Grid key={`attributes-${index}`} item container xs={12} spacing={2}>
        <Grid item xs={3}>
          <Typography variant='body1' data-test={`attribute-label-${index}`}>
            Attribute
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <Typography
            variant='body1'
            data-test={`attribute-name-label-${index}`}
          >
            Attribute Name
          </Typography>
        </Grid>
        <Grid item xs={3} data-test={`att-value-${index}`}>
          {att.Value}
        </Grid>
        <Grid item xs={9} data-test={`att-name-${index}`}>
          {att.Name}
        </Grid>
        {index + 1 !== arr.length && (
          <Grid item xs={12}>
            <Divider variant='fullWidth' />
          </Grid>
        )}
      </Grid>
    ))

  const handleBackClick = () => {
    onBackToListClick()
  }

  const handleEditClick = () => {
    setMode(COMPONENT_MODE.EDIT)
  }

  const handleCancelEdit = () => {
    setMode(COMPONENT_MODE.DETAIL)
    form.setValue('givenName', userDetail.givenName)
    form.setValue('familyName', userDetail.familyName)
    form.setValue('email', userDetail.email)
  }

  const handleSubmit = (data) => {
    if (featureIsDirty) {
      updateUserFeatures.trigger({
        username: user.username,
        tenantId,
        features: buildFeaturesPayload(features),
        role: role
      })
    }

    if (form.formState.isDirty && form.formState.isValid) {
      const { givenName, familyName, email } = data
      adminUpdateUser.trigger({
        username: userDetail?.username,
        tenantId,
        attributes: {
          given_name: givenName,
          family_name: familyName,
          email
        },
        verifyUrl: baseToolkitUrl
      })
    }
  }

  const givenName = isEdit
    ? <Input name='givenName' data-test='first-name-text-field' disabled={adminUpdateUser.isWorking} control={form.control} />
    : userDetail?.givenName

  const familyName = isEdit
    ? <Input name='familyName' data-test='last-name-text-field' disabled={adminUpdateUser.isWorking} control={form.control} />
    : userDetail?.familyName

  const email = isEdit
    ? <Input name='email' data-test='email-text-field' disabled={adminUpdateUser.isWorking} control={form.control} />
    : userDetail?.email

  return (
    <>
      <Button
        color='primary'
        size='small'
        onClick={() => handleBackClick()}
        data-test='user-detail-back-button'
        disabled={isLoading}
      >
        {' '}
        <ArrowBack /> Back to User List{' '}
      </Button>
      <Paper
        id='user-list'
        className={classes.root}
        tabIndex={0}
        aria-label='Content List'
        elevation={0}
        style={{ margin: 20, display: 'flex' }}
      >
        {!isLoading ? (
          userDetail && (
            <>
              <Box
                className={classes.leftBar}
              />
              <form onSubmit={form.handleSubmit(handleSubmit)}>

                <Grid container spacing={2} className={classes.paper}>
                  <Grid item xs={6}>
                    <Typography variant='h5' data-test='first-name-label'>
                      First Name
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5' data-test='last-name-label'>
                      Last Name
                    </Typography>
                  </Grid>
                  <Grid item xs={6} data-test='first-name'>
                    {givenName}
                  </Grid>
                  <Grid item xs={6} data-test='last-name'>
                    {familyName}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5' data-test='email-label'>
                      Email Address
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant='h5' data-test='username-label'>
                      Username
                    </Typography>
                  </Grid>
                  <Grid item xs={6} data-test='email'>
                    {email}
                  </Grid>
                  <Grid item xs={6} data-test='username'>
                    {userDetail?.username}
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='h5' data-test='creation-label'>
                      Creation Date
                    </Typography>
                  </Grid>
                  <Grid item xs={12} data-test='creation'>
                    {parseISO(userDetail?.createdAt).toLocaleDateString()}
                  </Grid>

                  {isAdminMode && (
                    <>
                      <Grid item xs={12}>
                        <Divider variant='fullWidth' />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='h5' data-test='role-label'>
                          User Role
                        </Typography>
                        <Grid item xs={12} data-test='role' className={classes.role}>
                          {role && (
                            <RoleSelector
                              user={user}
                              role={role}
                              setRole={setRole}
                              roles={roles}
                              userPoolId={userPoolId}
                              onRoleChanged={onRoleChanged}
                            />
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider variant='fullWidth' />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant='h5' data-test='email-suscription-label'>
                          Weekly Email Subscription
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant='h5' data-test='status-label'>
                          User Status
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        className={classes.suscription}
                        data-test='email-suscription'
                      >
                        {userHasWeeklySubscription ? 'Subscribed' : 'Unsubscribed'}
                      </Grid>
                      <Grid item xs={6} data-test='status'>
                        <ToggleUser
                          user={user}
                          userPoolId={userPoolId}
                          onToggle={value => setEnabled(value)}
                        />
                      </Grid>
                    </>
                  )}

                  {features.length > 0 && (
                    <>
                      <Grid item xs={12}>
                        <Divider variant='fullWidth' />
                      </Grid>
                      <Grid item xs={12}>
                        <UserFeatures
                          features={features}
                          loading={getUserFeatures.isWorking}
                          disabled={!isEdit}
                          setFeatures={setFeatures}
                          markAsDirty={setFeatureIsDirty}
                          userFeatures={userFeatures}
                          username={user.username}
                          profile={user.profile}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Divider variant='fullWidth' />
                  </Grid>
                  {!isEdit && (
                    <Grid item sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
                      <ActionButton
                        onClick={handleEditClick}
                        variant='secondary'
                        dataTest='edit-user-details-btn'
                      >
                        Edit user details
                      </ActionButton>
                    </Grid>
                  )}
                  {isEdit && (
                    <>
                      <Grid item>
                        <ActionButton
                          onClick={handleCancelEdit}
                          variant='secondary'
                          disabled={adminUpdateUser.isWorking}
                          dataTest='cancel-btn'
                        >
                          Cancel
                        </ActionButton>
                      </Grid>
                      <Grid item>
                        <ActionButton
                          // onClick={handleSubmit(onSubmit)}
                          variant='primary'
                          isWorking={adminUpdateUser.isWorking || updateUserFeatures.isWorking}
                          disabled={!(featureIsDirty || (form.formState.isDirty && form.formState.isValid))}
                          type='submit'
                          dataTest='save-changes-btn'
                        >
                          Save changes
                        </ActionButton>
                      </Grid>
                    </>
                  )}

                  {userDetail?.custom && (
                    <>
                      <Grid item xs={12}>
                        <Divider variant='fullWidth' />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant='h5' data-test='sso-attributes'>
                          SSO Attributes
                        </Typography>
                      </Grid>
                      {renderCustom()}
                    </>
                  )}

                  {!isSSOUser && (
                    <>
                      <Grid item xs={12}>
                        <Divider variant='fullWidth' />
                      </Grid>
                      <Grid item xs={6}>
                        <PasswordReset user={user} userPoolId={userPoolId} />
                      </Grid>
                    </>
                  )}
                </Grid>
              </form>
            </>
          )
        ) : (
          <Grid
            id='tenant-list-loading'
            container
            direction='row'
            justifyContent='center'
            alignItems='center'
            classes={{ root: classes.loadingAnimation }}
          >
            <CircularProgress size={60} color='primary' />
          </Grid>
        )}
      </Paper>
    </>
  )
}

export default UserDetail
